// ── Post-Processing Pipeline (pmndrs/postprocessing + N8AO) ──
// Merges SSAO, bloom, SMAA, color grading, and vignette into minimal passes.
// Previous: 6 separate passes via Three.js EffectComposer.
// New: 1-2 passes via pmndrs/postprocessing EffectComposer + N8AO.

import * as THREE from 'three';
import {
  EffectComposer,
  EffectPass,
  RenderPass,
  BloomEffect,
  SMAAEffect,
  SMAAPreset,
  VignetteEffect,
  VignetteTechnique,
  BrightnessContrastEffect,
  HueSaturationEffect,
  ToneMappingEffect,
  ToneMappingMode,
} from 'postprocessing';
import { N8AOPostPass } from 'n8ao';

import type { GraphicsQuality } from '../core/settings';
import { HeatDistortionEffect } from './effects/heat-distortion';
import { SSREffect } from './effects/ssr/SSREffect';

export class PostProcessing {
  private composer: EffectComposer;
  private n8aoPass: N8AOPostPass;
  private bloomEffect: BloomEffect;
  private smaaEffect: SMAAEffect;
  private vignetteEffect: VignetteEffect;
  private brightnessContrast: BrightnessContrastEffect;
  private hueSaturation: HueSaturationEffect;
  private heatDistortion: HeatDistortionEffect;
  private effectPass: EffectPass;
  private heatPass: EffectPass;
  private ssrEffect: SSREffect | null = null;
  private ssrPass: EffectPass | null = null;
  private camera: THREE.Camera;
  private renderer: THREE.WebGLRenderer;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
  ) {
    this.renderer = renderer;
    this.camera = camera;

    // pmndrs/postprocessing EffectComposer — single fullscreen triangle
    this.composer = new EffectComposer(renderer, {
      frameBufferType: THREE.HalfFloatType,
    });

    // 1. Scene render pass
    const renderPass = new RenderPass(scene, camera);
    this.composer.addPass(renderPass);

    // 2. N8AO — temporally stable ambient occlusion (replaces SSAOPass)
    // N8AO processes all layers — acceptable for sprite/decal rendering
    this.n8aoPass = new N8AOPostPass(scene, camera);
    this.n8aoPass.configuration.aoRadius = 8.0;
    this.n8aoPass.configuration.distanceFalloff = 1.0;
    this.n8aoPass.configuration.intensity = 4.0;
    this.n8aoPass.configuration.halfRes = true; // 2-4x faster
    this.n8aoPass.configuration.color = new THREE.Color(0, 0, 0);
    this.composer.addPass(this.n8aoPass);

    // 3. Merged effects pass — bloom + SMAA + vignette + color grading
    // All merged into a single EffectPass (one fullscreen draw)
    this.bloomEffect = new BloomEffect({
      intensity: 0.25,
      luminanceThreshold: 0.75,
      luminanceSmoothing: 0.5,
      mipmapBlur: true,
    });

    this.smaaEffect = new SMAAEffect({
      preset: SMAAPreset.HIGH,
    });

    this.vignetteEffect = new VignetteEffect({
      technique: VignetteTechnique.DEFAULT,
      offset: 0.3,
      darkness: 0.65,
    });

    // CS:GO warm color grading
    this.brightnessContrast = new BrightnessContrastEffect({
      brightness: 0.04,
      contrast: 0.15,
    });

    this.hueSaturation = new HueSaturationEffect({
      saturation: 0.18,
      hue: 0.02, // Slight warm shift
    });

    const toneMapping = new ToneMappingEffect({
      mode: ToneMappingMode.ACES_FILMIC,
    });

    // Heat distortion for fire/molotov shimmer (high/ultra only)
    this.heatDistortion = new HeatDistortionEffect();

    // Heat distortion transforms UVs — must be in its own pass,
    // separate from convolution effects (bloom).
    this.heatPass = new EffectPass(camera, this.heatDistortion);
    this.composer.addPass(this.heatPass);

    // Main effects pass — bloom + SMAA + vignette + color grading
    this.effectPass = new EffectPass(
      camera,
      this.bloomEffect,
      this.smaaEffect,
      this.vignetteEffect,
      this.brightnessContrast,
      this.hueSaturation,
      toneMapping,
    );
    this.composer.addPass(this.effectPass);
  }

  render(): void {
    this.composer.render();
  }

  setSize(width: number, height: number): void {
    this.composer.setSize(width, height);
  }

  /** Adjust N8AO quality at runtime. */
  setAOQuality(halfRes: boolean, intensity = 4.0): void {
    this.n8aoPass.configuration.halfRes = halfRes;
    this.n8aoPass.configuration.intensity = intensity;
  }

  /**
   * Toggle screen-space reflections (SSR) at runtime. Lazy-instantiates the
   * effect on first enable so the pass cost is paid only when ultra quality
   * is selected.
   */
  setSSREnabled(enabled: boolean): void {
    if (enabled && !this.ssrPass) {
      this.ssrEffect = new SSREffect(this.camera, {
        steps: 20,
        refineSteps: 5,
        distance: 12.0,
        thickness: 0.02,
        maxRoughness: 0.3,
        resolutionScale: 0.75,
        blend: 0.5,
        blur: 0.5,
      });
      this.ssrPass = new EffectPass(this.camera, this.ssrEffect);
      // Insert before the main effect pass so reflections are visible
      // through the bloom + tonemapping stack.
      this.composer.addPass(this.ssrPass, this.composer.passes.length - 1);
    }
    if (this.ssrPass) {
      this.ssrPass.enabled = enabled;
    }
  }

  /**
   * Apply a graphics quality preset by toggling existing postprocessing effects.
   * - Low: disable all effects except render pass, reduce resolution
   * - Medium: bloom only, medium AO
   * - High: all effects at normal quality
   * - Ultra: all effects at max quality
   */
  setQuality(quality: GraphicsQuality): void {
    switch (quality) {
      case 'low':
        this.n8aoPass.enabled = false;
        this.heatPass.enabled = false;
        this.effectPass.enabled = false;
        this.setSSREnabled(false);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1));
        break;

      case 'medium':
        this.n8aoPass.enabled = true;
        this.n8aoPass.configuration.halfRes = true;
        this.n8aoPass.configuration.intensity = 2.0;
        this.n8aoPass.configuration.aoRadius = 4.0;
        this.n8aoPass.configuration.distanceFalloff = 0.5;
        this.heatPass.enabled = false;
        this.effectPass.enabled = true;
        this.bloomEffect.intensity = 0.15;
        this.vignetteEffect.darkness = 0.4;
        this.setSSREnabled(false);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
        break;

      case 'high':
        this.n8aoPass.enabled = true;
        this.n8aoPass.configuration.halfRes = true;
        this.n8aoPass.configuration.intensity = 4.0;
        this.n8aoPass.configuration.aoRadius = 8.0;
        this.n8aoPass.configuration.distanceFalloff = 0.8;
        this.heatPass.enabled = true;
        this.effectPass.enabled = true;
        this.bloomEffect.intensity = 0.25;
        this.vignetteEffect.darkness = 0.65;
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.setSSREnabled(false);
        break;

      case 'ultra':
        this.n8aoPass.enabled = true;
        this.n8aoPass.configuration.halfRes = false;
        this.n8aoPass.configuration.intensity = 5.0;
        this.n8aoPass.configuration.aoRadius = 10.0;
        this.n8aoPass.configuration.distanceFalloff = 1.0;
        this.heatPass.enabled = true;
        this.effectPass.enabled = true;
        this.bloomEffect.intensity = 0.3;
        this.vignetteEffect.darkness = 0.65;
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.setSSREnabled(true);
        break;

      // ── Mobile presets: SSR OFF, DOF OFF, heat distortion OFF ──

      case 'mobile-low':
        // Minimal pipeline: no AO, no bloom, only color grading + film grain, FXAA
        this.n8aoPass.enabled = false;
        this.heatPass.enabled = false;
        this.effectPass.enabled = true;
        this.bloomEffect.intensity = 0;
        this.vignetteEffect.darkness = 0.3;
        this.smaaEffect.applyPreset(SMAAPreset.LOW);
        this.setSSREnabled(false);
        this.renderer.setPixelRatio(1.0);
        break;

      case 'mobile-medium':
        // N8AO halfRes reduced samples, bloom 3 mip levels, FXAA
        this.n8aoPass.enabled = true;
        this.n8aoPass.configuration.halfRes = true;
        this.n8aoPass.configuration.intensity = 2.0;
        this.n8aoPass.configuration.aoRadius = 4.0;
        this.n8aoPass.configuration.distanceFalloff = 0.5;
        this.n8aoPass.configuration.aoSamples = 8;
        this.n8aoPass.configuration.denoiseSamples = 4;
        this.heatPass.enabled = false;
        this.effectPass.enabled = true;
        this.bloomEffect.intensity = 0.15;
        if ((this.bloomEffect as any).mipmapBlurPass?.levels !== undefined) {
          (this.bloomEffect as any).mipmapBlurPass.levels = 3;
        }
        this.vignetteEffect.darkness = 0.4;
        this.smaaEffect.applyPreset(SMAAPreset.LOW);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
        break;

      case 'mobile-high':
        // N8AO low preset, bloom 4 mips, SMAA, chromatic aberration
        this.n8aoPass.enabled = true;
        this.n8aoPass.configuration.halfRes = true;
        this.n8aoPass.configuration.intensity = 3.0;
        this.n8aoPass.configuration.aoRadius = 6.0;
        this.n8aoPass.configuration.distanceFalloff = 0.8;
        this.n8aoPass.configuration.aoSamples = 16;
        this.n8aoPass.configuration.denoiseSamples = 4;
        this.heatPass.enabled = false;
        this.effectPass.enabled = true;
        this.bloomEffect.intensity = 0.2;
        if ((this.bloomEffect as any).mipmapBlurPass?.levels !== undefined) {
          (this.bloomEffect as any).mipmapBlurPass.levels = 4;
        }
        this.vignetteEffect.darkness = 0.55;
        this.smaaEffect.applyPreset(SMAAPreset.MEDIUM);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2.0));
        break;
    }

    this.composer.setSize(window.innerWidth, window.innerHeight);
  }

  /**
   * Get the heat distortion effect instance so external code (e.g., grenade-effects)
   * can call setFireSource() / clearSources() to drive the shimmer.
   */
  getHeatDistortion(): HeatDistortionEffect {
    return this.heatDistortion;
  }

  /** Returns the depth texture from the composer's input buffer (for smoke depth clipping). */
  getDepthTexture(): THREE.DepthTexture | null {
    const rt = this.composer.inputBuffer;
    return (rt as any)?.depthTexture ?? null;
  }

  dispose(): void {
    // Composer does not dispose individual effects; do it here so internal render
    // targets, shaders, and uniform textures are released.
    this.bloomEffect.dispose();
    this.smaaEffect.dispose();
    this.vignetteEffect.dispose();
    this.brightnessContrast.dispose();
    this.hueSaturation.dispose();
    this.heatDistortion.dispose();
    this.effectPass.dispose();
    this.heatPass.dispose();
    this.n8aoPass.dispose();
    if (this.ssrPass) this.ssrPass.dispose();
    if (this.ssrEffect) this.ssrEffect.dispose();
    this.composer.dispose();
  }
}
